<script setup>
/**
 * FormControlModulestructureColumnName
 * Composition-based wrapper around the standard text form control.
 * Safely aggregates record titles to generate database-safe camelCase identifiers
 * exclusively when building out brand-new structure elements.
 */
import {computed, getCurrentInstance, watch} from 'vue'
import FormControlTypeText from '@/common/form-control-type/FormControlTypeText.vue'
import {useFormStore} from '@/stores/form'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
})

const formStore = useFormStore(props.formId)

// --- Computed ---

const titleValue = computed(() => props.currentData?.title || '')
const columnNameValue = computed(() => props.currentData?.columnName || '')

// --- Methods ---

const formatValue = (str) => {
    if (!str) return ''
    return str
        // Remove all special characters
        .replace(/[^a-zA-Z 0-9_]+/g, '')
        // Replace all double spaces left by removing special chars
        .replace('  ', ' ')
        // Separate consecutive capital letters from a following camelCase word (e.g. "URLSlug" -> "URL_Slug")
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
        // Separate lowercase letters from following uppercase letters (e.g. "urlSlug" -> "url_Slug")
        .replace(/([a-z\d])([A-Z])/g, '$1_$2')
        // Replace spaces and hyphens with a single underscore
        .replace(/[\s-]+/g, '_')
        .toLowerCase()
}

// Although column names in Postgres are all snake_case, the API provides them in camelCase format.
// They must therefore be converted to snake_case to match the real column name.
// This approach is taken because the standard for JavaScript is to use camelCase, so providing all column names
// as camelCase they can be referenced throughout the codebase as o.propertyName without the need for
// conversion. This is the only place that it needs to be converted back.
formStore.presetData({
    name: props.field.name,
    value: formatValue(props.currentData[props.field.name])
})

// --- Watchers ---

watch(titleValue, (newVal, oldVal) => {
    // Only format the value when creating new fields.
    // This is so that modifying labels for existing fields does not alter their names.
    const currentColumnName = props.currentData[props.field.name]

    // If this field's current value is the formatted version of the old title value then
    // the values are currently in sync, so set it to the formatted version of the new value.
    if (currentColumnName === formatValue(oldVal) || currentColumnName === '') {
        props.currentData[props.field.name] = formatValue(newVal)
    }
})

watch(columnNameValue, (newVal, oldVal) => {
    // Prevents double processing when title change modifies column name
    if (oldVal !== newVal) {
        props.currentData[props.field.name] = formatValue(newVal)
    }
})
</script>

<template>
    <FormControlTypeText v-bind="$props" v-if="!field.hidden"/>
</template>